import BasePlayer from './base-player';
import { MediaData } from './types';
import './waveform';

// Style template for card audio player with default dimensions
const cardStyleTemplate = document.createElement('template');
cardStyleTemplate.innerHTML = `
  <style>
    :host {
      display: block;
      width: 100px;
      height: 100px;
      box-sizing: border-box;
    }
    media-controller {
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius, 8px);
      overflow: hidden;
      background-clip: padding-box;
    }
    media-controller[audio] {
      width: 100%;
      min-width: 100px;
      min-height: 100px;
    }
  </style>
`;

class KoiCardAudioPlayer extends BasePlayer {
  thumbnailUrl: string;
  showWaveform: boolean;
  constructor() {
    super();
    this.thumbnailUrl = '';
    this.showWaveform = false;
    this.shadowRoot.appendChild(cardStyleTemplate.content.cloneNode(true));
  }

  _mediaController?: HTMLElement;

  get mediaController(): HTMLElement | undefined {
    return this._mediaController;
  }

  renderPlayer(audioData: MediaData): void {
    this.hideLoadingState();
    if (this._mediaController) {
      const existingAudio = this._mediaController.querySelector('audio');
      if (existingAudio instanceof HTMLAudioElement) {
        existingAudio.setAttribute('src', audioData.mediaUrl);
        return;
      }
      this.unregisterMediaController(this._mediaController);
      this._mediaController.remove();
      this._mediaController = undefined;
    }

    const hostStyles = getComputedStyle(this);
    const getHostCssVar = (varName: string, fallback: string) => {
      const value = hostStyles.getPropertyValue(varName).trim();
      return value || fallback;
    };

    const divBackgroundColor = getHostCssVar('--div-background-color', 'rgba(0, 0, 0, 0.4)');
    const playButtonBackground = getHostCssVar(
      '--waveform-play-button-background',
      'rgba(255, 255, 255, 0.2)'
    );

    this._mediaController = document.createElement('media-controller');
    this.registerMediaController(this._mediaController);
    this._mediaController.setAttribute('audio', '');
    this._mediaController.setAttribute(
      'style',
      `
      box-sizing: border-box;
    `
    );

    const audio = document.createElement('audio');
    audio.setAttribute('slot', 'media');
    audio.setAttribute('src', audioData.mediaUrl);
    this._mediaController.appendChild(audio);

    // Create background (thumbnail or default black)
    const backgroundDiv = document.createElement('div');
    const waveformDiv = document.createElement('koi-waveform');
    if (this.thumbnailUrl) {
      backgroundDiv.setAttribute(
        'style',
        `
        display:flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-image: url(${this.thumbnailUrl});
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      `
      );
    } else if (this.showWaveform && audioData.waveform) {
      // Style the background div to contain the waveform
      backgroundDiv.setAttribute(
        'style',
        `
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: relative;
      `
      );

      // Style the waveform to fill the entire background
      waveformDiv.setAttribute('data', JSON.stringify(audioData.waveform));

      // Pass through waveform CSS variables from host
      const waveformVars = [
        '--waveform-background-color',
        '--waveform-center-line-color',
        '--waveform-played-color',
        '--waveform-unplayed-color',
        '--waveform-bar-thickness',
      ];

      const waveformStyles: string[] = [];

      waveformVars.forEach(varName => {
        const value = hostStyles.getPropertyValue(varName).trim();
        if (value) {
          waveformStyles.push(`${varName}: ${value}`);
        }
      });

      const baseWaveformStyle = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
      `;

      const finalStyle =
        waveformStyles.length > 0
          ? `${baseWaveformStyle}; ${waveformStyles.join('; ')}`
          : baseWaveformStyle;

      waveformDiv.setAttribute('style', finalStyle);
      backgroundDiv.appendChild(waveformDiv);

      // Connect waveform time updates
      const updateWaveformTime = () => {
        const waveform = waveformDiv as HTMLElement & {
          updateTime?: (currentTime: number, duration: number) => void;
        };
        if (waveform && typeof waveform.updateTime === 'function') {
          waveform.updateTime(audio.currentTime, audio.duration);
        }
      };

      // Listen for time updates
      audio.addEventListener('timeupdate', updateWaveformTime);
      audio.addEventListener('loadedmetadata', updateWaveformTime);

      // Listen for waveform seek events
      waveformDiv.addEventListener('waveform-seek', (event: Event) => {
        const customEvent = event as CustomEvent<{ targetTime: number; ratio: number }>;
        const { targetTime } = customEvent.detail;

        if (audio && !isNaN(targetTime) && targetTime >= 0) {
          audio.currentTime = targetTime;
        }
      });
    } else {
      backgroundDiv.setAttribute(
        'style',
        `
        display:flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-color: ${divBackgroundColor};
      `
      );
    }
    const playButton = document.createElement('media-play-button');
    playButton.setAttribute('notooltip', '');
    playButton.setAttribute(
      'style',
      `
      background: ${playButtonBackground};
      border-radius: 50%;
      padding: 10px;
      color: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      transition: all 0.2s ease;
      pointer-events: auto;
      position: relative;
      z-index: 2;
    `
    );

    backgroundDiv.appendChild(playButton);
    this._mediaController.appendChild(backgroundDiv);
    this.shadowRoot.appendChild(this._mediaController);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.type = 'audio';
    this.thumbnailUrl = this.getAttribute('thumbnail-url') || '';
    this.showWaveform = this.getAttribute('show-waveform') !== null;
    if (this.mediaId && this.stream) {
      console.error('KoiPlayer: Both media-id and stream provided. Only one should be used.');
      this.renderError(this.t('errors.bothMediaAndStream'));
      return;
    }

    if (this.mediaId && !this.orgId) {
      console.error('KoiPlayer: No org-id provided');
      this.renderError(this.t('errors.noOrgId'));
      return;
    }

    if (!this.mediaId && !this.stream) {
      console.error('KoiPlayer: No media-id or stream provided');
      this.renderError(this.t('errors.noMediaOrStream'));
      return;
    }

    if (this.mediaId) {
      this.showLoadingState(this.t('loading.audio'), { aspectRatio: '1 / 1', minHeight: 100 });
      this.fetchAudioData({
        mediaId: this.mediaId,
        orgId: this.orgId,
        env: this.env,
        showWaveform: this.showWaveform,
      })
        .then(audioData => {
          this.renderPlayer(audioData);
          this.meta.ready = true;
          this.finalizeSetup(true);
        })
        .catch(error => {
          console.error('KoiPlayer: Error fetching audio data', error);
          this.renderError(this.t('errors.errorLoadingAudio'));
        });
    } else if (this.stream) {
      this.renderPlayer({ mediaUrl: this.stream });
      this.meta.ready = true;
      this.finalizeSetup(true);
    }
  }
}

export default KoiCardAudioPlayer;
